import { TFunction } from 'i18next';
import { DateOptions } from 'store/api/enums';
import {
  AdvancedUserRightsCodes,
  ContactType,
  DataLogActions,
  DataLogStatus,
  ChartOfAccountType,
  ExcelTransactionAccountType,
  FilterAccounTypes,
  TaxRateStatus,
  ViesStatus,
  ViesTriggerType,
} from 'store/app/enums';
import { AdvancedUserRights } from 'store/user/models';

const DATA_LOG_STATUS_LABEL_KEYS: Record<string, string> = {
  [DataLogStatus.SAVED]: 'main.dataLogStatus.saved',
  [DataLogStatus.LOCKED]: 'main.dataLogStatus.locked',
  [DataLogStatus.SUBMITTED]: 'main.dataLogStatus.submitted',
  [DataLogStatus.CANCELLED]: 'main.dataLogStatus.cancelled',
  [DataLogStatus.AWAITING_APPROVAL]: 'main.dataLogStatus.awaiting-approval',
  [DataLogStatus.INTERNAL_APPROVED]: 'main.dataLogStatus.internal-approved',
  [DataLogStatus.APPROVED]: 'main.dataLogStatus.approved',
  [DataLogStatus.AWAITING_CLIENT_APPROVAL]: 'main.awaiting-client',
  [DataLogStatus.CLIENT_APPROVED]: 'main.dataLogStatus.client-approved',
  [DataLogStatus.NOT_SAVED]: 'main.draft',
  [DataLogStatus.EXCEPTIONS_MODIFIED]: 'main.dataLogStatus.exceptions-modified',
  [DataLogStatus.NOT_STARTED]: 'main.notStarted',
};

const VIES_STATUS_LABEL_KEYS: Record<string, string> = {
  [ViesStatus.VALID]: 'main.valid',
  [ViesStatus.INVALID]: 'main.invalid',
  [ViesStatus.ERROR]: 'common.error',
  [ViesStatus.EMPTY]: 'main.missing',
  [ViesStatus.MISSING]: 'main.missing',
  [ViesStatus.SYSTEM_UNAVAILABLE]: 'main.system-unavailable',
};

const VIES_TRIGGER_TYPE_LABEL_KEYS: Record<string, string> = {
  [ViesTriggerType.WEBHOOK]: 'vat-checker.trigger-type.webhook',
  [ViesTriggerType.CONTACT_REFRESH]: 'vat-checker.trigger-type.contact-refresh',
  [ViesTriggerType.CONTACT_CREATE]: 'vat-checker.trigger-type.contact-create',
  [ViesTriggerType.SINGLE_CONTACT_VALIDATION]:
    'vat-checker.trigger-type.manual-refresh',
  [ViesTriggerType.ALL_CONTACTS_VALIDATION]:
    'vat-checker.trigger-type.manual-refresh',
  [ViesTriggerType.TAX_GENERATION]: 'vat-checker.trigger-type.tax-generation',
};

const CONTACT_TYPE_LABEL_KEYS: Record<string, string> = {
  [ContactType.CUSTOMER]: 'contacts.contact-type.customer',
  [ContactType.SUPPLIER]: 'contacts.contact-type.supplier',
  [ContactType.BOTH]: 'contacts.contact-type.both',
  [ContactType.NULL]: 'contacts.contact-type.none',
};

const DATE_OPTION_LABEL_KEYS: Record<string, string> = {
  [DateOptions.ALL]: 'common.all',
  [DateOptions.TODAY]: 'submissions.today',
  [DateOptions.THIS_MONTH]: 'main.date-options.this-month',
  [DateOptions.LAST_MONTH]: 'main.date-options.last-month',
  [DateOptions.NEXT_MONTH]: 'main.date-options.next-month',
  [DateOptions.THIS_WEEK]: 'main.date-options.this-week',
  [DateOptions.LAST_WEEK]: 'main.date-options.last-week',
  [DateOptions.NEXT_WEEK]: 'main.date-options.next-week',
  [DateOptions.THIS_QUARTER]: 'main.date-options.this-quarter',
  [DateOptions.NEXT_QUARTER]: 'main.date-options.next-quarter',
  [DateOptions.THIS_YEAR]: 'main.date-options.this-year',
  [DateOptions.LAST_YEAR]: 'main.date-options.last-year',
};

const EXCEL_TRANSACTION_ACCOUNT_TYPE_LABEL_KEYS: Record<string, string> = {
  [ExcelTransactionAccountType.INCOME]:
    'organisation.excel-account-type.income',
  [ExcelTransactionAccountType.EXPENSES]:
    'organisation.excel-account-type.expenses',
  [ExcelTransactionAccountType.COST_OF_SALES]:
    'organisation.excel-account-type.cost-of-sales',
  [ExcelTransactionAccountType.DEPRECIATION]:
    'organisation.excel-account-type.depreciation',
};

const TAX_RATE_STATUS_LABEL_KEYS: Record<string, string> = {
  [TaxRateStatus.ACTIVE]: 'common.active',
  [TaxRateStatus.DELETED]: 'main.deleted',
  [TaxRateStatus.ARCHIVED]: 'main.archived',
};

const ADVANCED_USER_RIGHT_LABEL_KEYS: Record<string, string> = {
  [AdvancedUserRightsCodes.CANCEL_SUBMISSION]:
    'users.advanced-rights.cancel-submission',
  [AdvancedUserRightsCodes.CREATE_SUBMISSION]:
    'users.advanced-rights.create-submission',
  [AdvancedUserRightsCodes.CREATE_EDIT_RETURN]:
    'users.advanced-rights.create-edit-return',
  [AdvancedUserRightsCodes.VAT_NUMBER_CHECKER]:
    'navigation.vat-number-checker',
  [AdvancedUserRightsCodes.CREATE_TAX_RATES]:
    'users.advanced-rights.create-tax-rates',
  [AdvancedUserRightsCodes.APPROVE_SUBMISSION]:
    'users.advanced-rights.approve-submission',
  [AdvancedUserRightsCodes.GENERATE_RECONCILIATION]:
    'users.advanced-rights.generate-reconciliation',
};

const ADVANCED_USER_RIGHT_NAME_FALLBACK_KEYS: Record<string, string> = {
  'Cancel Submissions': 'users.advanced-rights.cancel-submission',
  'Create Submissions': 'users.advanced-rights.create-submission',
  'Manage Returns': 'users.advanced-rights.create-edit-return',
  'VAT Number Checker': 'navigation.vat-number-checker',
  'Create Tax Rates': 'users.advanced-rights.create-tax-rates',
  'Approve Submissions': 'users.advanced-rights.approve-submission',
  'Create Reconciliation': 'users.advanced-rights.generate-reconciliation',
};

const DATA_LOG_ACTION_LABEL_KEYS: Record<DataLogActions, string> = {
  [DataLogActions.REVERT_DATA]: 'vat-report.data-log-actions.revert-data',
  [DataLogActions.SAVE_VAT_RETURN]: 'vat-report.save-submission',
  [DataLogActions.CANCEL_RETURN]: 'vat-report.data-log-actions.cancel-return',
  [DataLogActions.LOCK_AS_APPROVED]: 'vat-report.data-log-actions.lock-as-approved',
  [DataLogActions.SET_AWAITING_APPROVAL]:
    'vat-report.data-log-actions.set-awaiting-approval',
  [DataLogActions.SET_INTERNAL_APPROVED]:
    'vat-report.data-log-actions.set-internal-approved',
  [DataLogActions.SET_NOT_APPROVED]: 'vat-report.data-log-actions.set-not-approved',
  [DataLogActions.SEND_FOR_CLIENT_APPROVAL]:
    'vat-report.data-log-actions.send-for-client-approval',
  [DataLogActions.SET_CLIENT_APPROVED]:
    'vat-report.data-log-actions.set-client-approved',
  [DataLogActions.MARK_AS_SUBMITTED]: 'vat-report.submitted-with-authorities',
};

export function getDataLogStatusLabel(
  statusValue: string,
  t: TFunction
): string {
  const key = DATA_LOG_STATUS_LABEL_KEYS[statusValue];
  return key ? t(key) : statusValue;
}

export function getViesStatusLabel(statusValue: string, t: TFunction): string {
  const key = VIES_STATUS_LABEL_KEYS[statusValue];
  return key ? t(key) : statusValue;
}

export function getViesTriggerTypeLabel(
  triggerType: string,
  t: TFunction
): string {
  const enumValue =
    ViesTriggerType[triggerType as keyof typeof ViesTriggerType] ?? triggerType;
  const key = VIES_TRIGGER_TYPE_LABEL_KEYS[enumValue];
  return key ? t(key) : enumValue;
}

export function getContactTypeLabel(
  contactType: string,
  t: TFunction
): string {
  const enumValue =
    ContactType[contactType as keyof typeof ContactType] ?? contactType;
  const key = CONTACT_TYPE_LABEL_KEYS[enumValue];
  return key ? t(key) : enumValue;
}

export function getContactTypeOptions(t: TFunction) {
  return (Object.keys(ContactType) as Array<keyof typeof ContactType>).map(
    (key) => ({
      label: getContactTypeLabel(key, t),
      value: key,
    })
  );
}

export function getDateOptionLabel(dateOption: string, t: TFunction): string {
  const key = DATE_OPTION_LABEL_KEYS[dateOption];
  return key ? t(key) : dateOption;
}

function lookupAccountTypeLabel(
  map: Record<string, string>,
  raw: string
): string | undefined {
  return map[raw] || map[raw.toLowerCase()] || map[raw.toUpperCase()];
}

/** Provider type keys (`stock`, `COST_OF_GOODS_SOLD`) → "Stock", "Cost Of Goods Sold". */
function humanizeAccountTypeKey(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '-';
  return trimmed
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getExcelTransactionAccountTypeLabel(
  accountType: string,
  t: TFunction
): string {
  const raw = String(accountType ?? '').trim();
  if (!raw) return '-';

  const enumValue =
    ExcelTransactionAccountType[
      raw as keyof typeof ExcelTransactionAccountType
    ] ?? raw;
  const excelKey = EXCEL_TRANSACTION_ACCOUNT_TYPE_LABEL_KEYS[enumValue];
  if (excelKey) return t(excelKey);

  const chartLabel = lookupAccountTypeLabel(
    ChartOfAccountType as Record<string, string>,
    raw
  );
  if (chartLabel) return chartLabel;

  const providerLabel = lookupAccountTypeLabel(
    FilterAccounTypes as Record<string, string>,
    raw
  );
  if (providerLabel) return providerLabel;

  return humanizeAccountTypeKey(enumValue);
}

export function getExcelTransactionAccountTypeOptions(t: TFunction) {
  const seenLabels = new Set<string>();
  return (
    Object.keys(ChartOfAccountType) as Array<keyof typeof ChartOfAccountType>
  )
    .map((key) => ({
      label: getExcelTransactionAccountTypeLabel(key, t),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter((option) => {
      const label = option.label.toLowerCase();
      if (seenLabels.has(label)) return false;
      seenLabels.add(label);
      return true;
    });
}

export function getTaxRateStatusLabel(
  statusValue: string,
  t: TFunction
): string {
  const key = TAX_RATE_STATUS_LABEL_KEYS[statusValue];
  return key ? t(key) : statusValue;
}

export function getDisplayStatusLabel(
  statusValue: string,
  t: TFunction
): string {
  if (DATA_LOG_STATUS_LABEL_KEYS[statusValue]) {
    return getDataLogStatusLabel(statusValue, t);
  }
  if (VIES_STATUS_LABEL_KEYS[statusValue]) {
    return getViesStatusLabel(statusValue, t);
  }
  if (TAX_RATE_STATUS_LABEL_KEYS[statusValue]) {
    return getTaxRateStatusLabel(statusValue, t);
  }
  return statusValue;
}

export function getDataLogActionLabel(
  action: DataLogActions,
  t: TFunction
): string {
  const key = DATA_LOG_ACTION_LABEL_KEYS[action];
  return key ? t(key) : action;
}

export function getAdvancedUserRightLabel(
  right: Pick<AdvancedUserRights, 'operationRightCode' | 'name'>,
  t: TFunction
): string {
  const codeKey = ADVANCED_USER_RIGHT_LABEL_KEYS[right.operationRightCode];
  if (codeKey) return t(codeKey);
  const nameKey = ADVANCED_USER_RIGHT_NAME_FALLBACK_KEYS[right.name];
  if (nameKey) return t(nameKey);
  return right.name;
}
